// src/pages/CircleDetailPage.tsx
//
// CRM Circle detail with tabs: Overview / People / Activity / Settings.
// Reads from RTK Query; realtime CIRCLE_* events invalidate the cache
// through useCircleRealtime.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useGetCircleQuery,
  useGetCirclePeopleQuery,
  useGetCirclePeopleCandidatesQuery,
  useGetCirclesQuery,
  useGetWorkspaceCentralTeamsQuery,
  useUpdateCircleMutation,
  useArchiveCircleMutation,
  useRestoreCircleMutation,
  useDeleteCircleMutation,
  useAddCircleMembersMutation,
  useRemoveCircleMemberMutation,
  useAddCircleCentralMemberMutation,
  useUpdateCircleCentralMemberMutation,
  useRemoveCircleCentralMemberMutation,
} from '../api/circlesApi';
import { useGetContactsQuery } from '../api/contactsApi';
import { useGetEntityActivityQuery } from '../api/activityApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useCircleRealtime } from '../hooks/useCircleRealtime';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import CircleTypeBadge from '../components/circles/CircleTypeBadge';
import CircleVisibilityBadge from '../components/circles/CircleVisibilityBadge';
import CircleFormFields, {
  circleToFormValues,
  type CircleFormValues,
} from '../components/circles/CircleFormFields';
import SmartRulesSummary from '../components/circles/SmartRulesSummary';
import { smartRuleErrorsFrom, smartRulesForSubmit } from '../components/circles/smartRules';
import ConfirmDialog, { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import type {
  CentralTeamRole,
  Circle,
  CircleCentralCandidate,
  CircleCentralMember,
  CircleContactMember,
  Contact,
  ContactFilterError,
  SmartCircleMember,
  UpdateCircleRequest,
} from '../types/api';
import { CENTRAL_TEAM_ROLES } from '../types/api';
import CircleProvidersTab from '../components/providerAssignments/CircleProvidersTab';
import CircleAssignmentsTab from '../components/providerAssignments/CircleAssignmentsTab';
import MemberProviderChips from '../components/providerAssignments/MemberProviderChips';
import ManageMemberProvidersModal from '../components/providerAssignments/ManageMemberProvidersModal';
import { readEntitlementError } from '../utils/entitlementErrors';
import MemberProviderAccessSection, {
  deriveProviderCatalog,
} from '../components/providerAssignments/MemberProviderAccessSection';
import CircleVoiceCallModal from '../components/circles/CircleVoiceCallModal';
import { useCircleDialogFocus } from '../components/circles/useCircleDialogFocus';
import ActiveCallShell from '../components/telephony/ActiveCallShell';
import {
  useGetCirclePeopleProvidersQuery,
  useGetCircleProvidersQuery,
  useMintCircleVoiceTokenMutation,
} from '../api/circlesApi';
import { useAuth } from '../hooks/useAuth';
import { useCircleOwnerCandidates } from '../components/circles/useCircleOwnerCandidates';
import { useTelephonyAvailability } from '../hooks/useTelephonyAvailability';
import { useTwilioVoiceDevice } from '../hooks/useTwilioVoiceDevice';
import { useProviderAssignmentRealtime } from '../hooks/useProviderAssignmentRealtime';
import SubjectComplianceSection from '../components/compliance/SubjectComplianceSection';
import { useHasCapability } from '../services/centralContext';

type TabKey =
  | 'overview'
  | 'people'
  | 'providers'
  | 'assignments'
  | 'compliance'
  | 'activity'
  | 'settings';

const CIRCLE_TYPES_WITH_ASSIGNMENTS = new Set(['department', 'team']);

// The Providers + Assignments tabs are only meaningful for Circles that
// represent an organizational unit — hidden on Custom / People Group /
// Smart Segment / Territory / Account Team.
//
// Compliance is only shown for department circles (they can be a
// subject scope) and requires compliance.view.
function buildTabsForCircle(
  circle: Circle,
  opts: { canViewCompliance: boolean },
): Array<{ key: TabKey; label: string }> {
  const base: Array<{ key: TabKey; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'people', label: 'People' },
  ];
  if (CIRCLE_TYPES_WITH_ASSIGNMENTS.has(circle.type)) {
    base.push({ key: 'providers', label: 'Providers' });
    base.push({ key: 'assignments', label: 'Assignments' });
  }
  // Phase 12.3 fix — both Department AND Team Circles are Central-
  // Team-backed organizational Circles and use the same
  // `department_circle` compliance subject. Mobile already renders
  // Compliance for both types (see mobile/CircleDetailScreen.tsx:236);
  // the web previously gated on department only, hiding compliance
  // for team-type Circles. Aligned here to match.
  if (
    opts.canViewCompliance &&
    (circle.type === 'department' || circle.type === 'team')
  ) {
    base.push({ key: 'compliance', label: 'Compliance' });
  }
  base.push({ key: 'activity', label: 'Activity' });
  base.push({ key: 'settings', label: 'Settings' });
  return base;
}

export default function CircleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  useCircleRealtime({ workspaceId, activeCircleId: id });
  useProviderAssignmentRealtime({ workspaceId, departmentCircleId: id });
  const canViewCompliance = useHasCapability('compliance', 'view');

  const {
    data: circle,
    isLoading,
    error,
    refetch,
  } = useGetCircleQuery(id!, { skip: !id });

  if (!id) {
    return <ErrorState message="Missing circle id." />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    const status = (error as { status?: number })?.status;
    if (status === 403) {
      return (
        <div className="text-center py-16">
          <p className="text-gray-600">You do not have access to this circle.</p>
          <Link to="/circles" className="mt-3 inline-block text-indigo-600 hover:underline">
            Back to Circles
          </Link>
        </div>
      );
    }
    if (status === 404) {
      return (
        <div className="text-center py-16">
          <p className="text-gray-600">Circle not found or deleted.</p>
          <Link to="/circles" className="mt-3 inline-block text-indigo-600 hover:underline">
            Back to Circles
          </Link>
        </div>
      );
    }
    return <ErrorState message="Failed to load circle." onRetry={refetch} />;
  }

  if (!circle) {
    return <ErrorState message="Circle not found." />;
  }

  return (
    <div className="space-y-6" data-testid="circle-detail-page">
      <CircleHeader circle={circle} onBack={() => navigate('/circles')} />

      {circle.archivedAt && (
        <div
          role="status"
          className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800"
          data-testid="circle-archived-banner"
        >
          This circle is archived. It won't appear in the default list until restored.
        </div>
      )}

      <div role="tablist" aria-label="Circle sections" className="border-b border-gray-200">
        <div className="flex gap-6">
          {buildTabsForCircle(circle, { canViewCompliance }).map((tab) => (
            <button
              key={tab.key}
              role="tab"
              type="button"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`circle-tab-${tab.key}`}
              className={`pb-3 -mb-px text-sm font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-t ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div role="tabpanel">
        {activeTab === 'overview' && <OverviewTab circle={circle} />}
        {activeTab === 'people' && <PeopleTab circle={circle} workspaceId={workspaceId} />}
        {activeTab === 'providers' && (
          <CircleProvidersTab circle={circle} workspaceId={workspaceId} />
        )}
        {activeTab === 'assignments' && (
          <CircleAssignmentsTab circle={circle} workspaceId={workspaceId} />
        )}
        {activeTab === 'compliance' && workspaceId && (
          <SubjectComplianceSection
            workspaceId={workspaceId}
            subjectType="department_circle"
            subjectId={circle.id}
          />
        )}
        {activeTab === 'activity' && <ActivityTab circleId={circle.id} />}
        {activeTab === 'settings' && (
          <SettingsTab
            circle={circle}
            workspaceId={workspaceId}
            onDeleted={() => navigate('/circles')}
          />
        )}
      </div>

      {/* Phase 12.9 — reused ActiveCallShell for outbound Circle-native
          calls. Renders on this page whenever an outbound call is active
          so the user sees mute/hangup/duration without leaving the Circle
          context. Uses the SHARED Twilio Device via useTwilioVoiceDevice
          — never spawns a rival Device. */}
      <CircleActiveCallOverlay />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CircleActiveCallOverlay — outbound-call reused shell for the Circle page.
// Inbound calls are still owned by the globally-mounted GlobalIncomingCallUI
// on the app root; this overlay renders ONLY for outbound calls. Placed at
// the page level so it survives tab switches inside the Circle.
// ---------------------------------------------------------------------------
function CircleActiveCallOverlay() {
  const voice = useTwilioVoiceDevice();
  const { activeCall, status } = voice;

  if (activeCall.kind === 'idle') return null;
  if (activeCall.direction === 'inbound') return null;

  const workspaceRecordingMode: 'off' | 'always' | undefined =
    status.kind === 'ready' ? status.recordingMode : undefined;

  return (
    <ActiveCallShell
      activeCall={activeCall}
      workspaceRecordingMode={workspaceRecordingMode}
      onMute={voice.mute}
      onUnmute={voice.unmute}
      onEndCall={voice.endCall}
      onDismiss={voice.clearActiveCall}
      onSendDigit={voice.sendDigits}
      audioDevices={voice.audioDevices}
      onSelectInput={voice.setInputDevice}
      onSelectOutput={voice.setOutputDevice}
    />
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function CircleHeader({ circle, onBack }: { circle: Circle; onBack: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
        >
          ← Circles
        </button>
        {circle.color && (
          <span
            aria-hidden
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: circle.color }}
          />
        )}
        <h1 className="text-2xl font-bold text-gray-900 truncate">{circle.name}</h1>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <CircleTypeBadge type={circle.type} />
        <CircleVisibilityBadge visibility={circle.visibility} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ circle }: { circle: Circle }) {
  // Mirror of CircleListCard's rule: for department/team Circles Central
  // is the authoritative source. `centralMemberCount` = null means
  // Central failure OR unlinked org Circle — render "—" rather than a
  // misleading 0 or a CRM-CircleMember-derived count.
  const isCentralAuthoritative =
    (circle.type === 'department' || circle.type === 'team') &&
    circle.centralMemberCount !== undefined;
  // Smart Circle: the matching contacts THIS viewer may see (server-scoped).
  const isSmart = circle.type === 'smart_segment';
  const memberCount = isSmart
    ? circle.smartMemberCount ?? null
    : isCentralAuthoritative
      ? circle.centralMemberCount ?? null
      : circle.members?.length ?? 0;
  const [updateCircle, { isLoading: isUnlinking }] = useUpdateCircleMutation();
  // The parent's NAME, read through the same record-scoped endpoint as any
  // Circle — a parent the viewer may not see renders as "a Circle you can't
  // open" rather than leaking its name or showing a raw id.
  const { data: parentCircle, isError: parentHidden } = useGetCircleQuery(circle.parentCircleId ?? '', {
    skip: !circle.parentCircleId,
  });
  const { data: teamsData } = useGetWorkspaceCentralTeamsQuery(undefined, {
    // No need to fetch teams when this circle isn't linked; the "None"
    // state renders without any lookup.
    skip: !circle.centralTeamId,
  });

  // Owner by NAME from the workspace roster; never a raw user id.
  const owners = useCircleOwnerCandidates(circle.workspaceId);
  const ownerName = useMemo(() => {
    const m = owners.members.find((x) => x.id === circle.ownerUserId);
    return m ? m.fullName?.trim() || m.email?.trim() || null : null;
  }, [owners.members, circle.ownerUserId]);

  const linkedTeam = useMemo(() => {
    if (!circle.centralTeamId || !teamsData?.teams) return null;
    return teamsData.teams.find((t) => t.id === circle.centralTeamId) ?? null;
  }, [circle.centralTeamId, teamsData]);

  const handleUnlink = async () => {
    try {
      await updateCircle({
        id: circle.id,
        data: { centralTeamId: null },
      }).unwrap();
    } catch {
      // Non-fatal — user can retry from the Settings tab.
    }
  };

  return (
    <div className="space-y-6" data-testid="circle-tabpanel-overview">
      {isSmart && (
        <section aria-labelledby="circle-smart-rules-heading" className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 id="circle-smart-rules-heading" className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Rules
          </h3>
          <div className="mt-1">
            <SmartRulesSummary circle={circle} />
          </div>
        </section>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <Field label="Description">
          {circle.description || <span className="text-gray-500">No description</span>}
        </Field>
        <Field label="Members">
          <span data-testid="circle-overview-member-count">
            {memberCount === null ? '—' : memberCount}
          </span>
          {isSmart && (
            <span className="block text-xs text-gray-500">
              Matching contacts you can see. Updates automatically.
            </span>
          )}
        </Field>
        <Field label="Icon">
          {circle.icon || <span className="text-gray-500">—</span>}
        </Field>
        <Field label="Color">
          {circle.color ? (
            <span className="inline-flex items-center gap-2">
              <span
                aria-hidden
                className="w-4 h-4 rounded border border-gray-200"
                style={{ backgroundColor: circle.color }}
              />
              <span>{circle.color}</span>
            </span>
          ) : (
            <span className="text-gray-500">—</span>
          )}
        </Field>
        {/* Owner affects who can SEE a Private Circle; it grants no edit
            rights and no record access. Shown in words, never as a raw id. */}
        <Field label="Owner">
          {!circle.ownerUserId ? (
            <span className="text-gray-500">—</span>
          ) : ownerName ? (
            <span data-testid="circle-owner-name">
              {ownerName}
              {circle.ownerUserId === circle.createdByUserId ? ' (creator)' : ''}
            </span>
          ) : circle.ownerUserId === circle.createdByUserId ? (
            'The creator'
          ) : (
            'Another workspace member'
          )}
        </Field>
        <Field label="Parent Circle">
          {!circle.parentCircleId ? (
            <span className="text-gray-500">—</span>
          ) : parentHidden ? (
            <span className="text-gray-500">A Circle you can’t open</span>
          ) : (
            <Link to={`/circles/${circle.parentCircleId}`} className="text-indigo-600 hover:underline">
              {parentCircle?.name ?? 'Loading…'}
            </Link>
          )}
        </Field>
        <Field label="Central Team">
          {circle.centralTeamId ? (
            <span className="inline-flex items-center gap-2" data-testid="circle-central-team-chip">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                {linkedTeam?.name ?? (teamsData ? 'Unknown team' : 'Loading…')}
                {linkedTeam?.status === 'archived' && (
                  <span className="text-[10px] text-indigo-500">(archived)</span>
                )}
              </span>
              {/* Phase 12 — Unlink is a Circle-management mutation on a
                  Central-backed Circle. Only the active Head sees it. */}
              {circle.canManageAsHead === true && (
                <button
                  type="button"
                  onClick={handleUnlink}
                  disabled={isUnlinking}
                  className="text-xs text-slate-500 hover:text-red-600 disabled:opacity-50"
                  data-testid="circle-central-team-unlink"
                  aria-label="Unlink Central Team"
                >
                  {isUnlinking ? 'Unlinking…' : 'Unlink'}
                </button>
              )}
            </span>
          ) : (
            <span className="text-gray-500">Not linked</span>
          )}
        </Field>
        <Field label="Created">{new Date(circle.createdAt).toLocaleString()}</Field>
        <Field label="Updated">{new Date(circle.updatedAt).toLocaleString()}</Field>
      </dl>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// People tab
// ---------------------------------------------------------------------------

// PeopleTab — source-discriminated router.
//
// Fetches the effective roster from `/api/circles/:id/people`. Central-
// backed circles render a READ-ONLY Central Team roster (no Add/Remove,
// no Contact picker); everything else renders the historical CRM-Contact
// editor. A Central failure NEVER falls back to Contacts — the empty
// state shows a typed error banner instead.
function PeopleTab({ circle, workspaceId }: { circle: Circle; workspaceId?: string }) {
  const { data: people, isLoading, isError, refetch } = useGetCirclePeopleQuery(circle.id);

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="circle-tabpanel-people">
        <p className="text-sm text-slate-500">Loading members…</p>
      </div>
    );
  }
  if (isError || !people) {
    return (
      <div className="space-y-4" data-testid="circle-tabpanel-people">
        <ErrorState
          title="Failed to load members"
          message="Please try again."
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (people.source === 'central_team') {
    return (
      <CentralTeamPeopleView
        circle={circle}
        members={people.members}
        canManage={people.canManage}
        centralError={people.centralError}
        onRetry={() => refetch()}
      />
    );
  }
  if (people.source === 'central_team_unlinked') {
    return (
      <UnlinkedCentralTeamPeopleView
        circle={circle}
        canLink={people.canLink}
      />
    );
  }
  if (people.source === 'smart_segment') {
    return (
      <SmartPeopleView
        circle={circle}
        rulesActive={people.rulesActive}
        total={people.total}
        members={people.members}
      />
    );
  }
  return (
    <ContactPeopleView
      circle={circle}
      workspaceId={workspaceId}
      members={people.members}
    />
  );
}

// -----------------------------------------------------------------------------
// SmartPeopleView — a Smart Circle's members: the contacts matching its rules
// that THE VIEWER may see, evaluated by the server on every read. Read-only:
// nobody is added or removed by hand; the rules decide.
// -----------------------------------------------------------------------------

function SmartPeopleView({
  circle,
  rulesActive,
  total,
  members,
}: {
  circle: Circle;
  rulesActive: boolean;
  total: number;
  members: SmartCircleMember[];
}) {
  return (
    <div className="space-y-4" data-testid="circle-tabpanel-people" data-people-source="smart_segment">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <SmartRulesSummary circle={circle} />
        {rulesActive && (
          <p className="mt-2 text-xs text-gray-500">
            Members update automatically as contacts change. You see the matching contacts you have
            access to. To change who is in this Circle, edit its rules in Settings.
          </p>
        )}
      </div>
      {rulesActive && (
        <>
          <p className="text-sm text-gray-700" data-testid="circle-smart-member-total">
            {total === 1 ? '1 matching contact' : `${total} matching contacts`}
            {total > members.length ? ` — showing the first ${members.length}` : ''}
          </p>
          {members.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl shadow-sm" data-testid="circle-people-empty">
              <p className="text-gray-500">No contacts you can see match these rules right now.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 bg-white rounded-xl shadow-sm" aria-label="Matching contacts">
              {members.map((m) => (
                <li key={m.id} className="flex min-w-0 flex-col px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <span className="truncate text-sm font-medium text-gray-900">{m.displayName}</span>
                  {m.email && <span className="truncate text-xs text-gray-500">{m.email}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// UnlinkedCentralTeamPeopleView — shown for department/team Circles that
// have no `centralTeamId`. NEVER renders Contacts. If the caller has
// permission, opens a Link modal that patches the Circle with a
// `centralTeamId` selected from Central workspace teams.
// -----------------------------------------------------------------------------

function typeLabel(t: Circle['type']): string {
  if (t === 'department') return 'Department';
  if (t === 'team') return 'Team';
  return 'Circle';
}

function UnlinkedCentralTeamPeopleView({
  circle,
  canLink,
}: {
  circle: Circle;
  canLink: boolean;
}) {
  const [showLink, setShowLink] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const label = typeLabel(circle.type);
  return (
    <div
      className="space-y-4"
      data-testid="circle-tabpanel-people"
      data-people-source="central_team_unlinked"
    >
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
        data-testid="circle-people-unlinked-banner"
      >
        <p className="font-medium">This {label} is not linked to a Central Team.</p>
        <p className="mt-1 text-xs">
          {label} members come from Central. Link a Central Team to see and manage members here.
        </p>
        {canLink && (
          <div className="mt-2">
            <Button
              onClick={() => {
                setBanner(null);
                setShowLink(true);
              }}
              data-testid="circle-link-central-team-btn"
            >
              Link Central Team
            </Button>
          </div>
        )}
      </div>

      {banner && (
        <div
          role="alert"
          className={`rounded-lg border px-3 py-2 text-sm ${
            banner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {banner.message}
        </div>
      )}

      {showLink && (
        <LinkCentralTeamModal
          circle={circle}
          onClose={() => setShowLink(false)}
          onResult={(result) => {
            setBanner(result);
            setShowLink(false);
          }}
        />
      )}
    </div>
  );
}

function LinkCentralTeamModal({
  circle,
  onClose,
  onResult,
}: {
  circle: Circle;
  onClose: () => void;
  onResult: (b: { kind: 'success' | 'error'; message: string }) => void;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Phase 12 — Head-only. Only Teams where the caller is an active
  // Head are returned; backend independently re-verifies on the write.
  const { data, isLoading, isError, refetch } = useGetWorkspaceCentralTeamsQuery({
    onlyHeaded: true,
  });
  const [updateCircle, { isLoading: isSaving }] = useUpdateCircleMutation();

  const teams = data?.teams ?? [];
  const available = data?.available !== false;
  const dialogRef = useCircleDialogFocus(onClose, isSaving);

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (!selectedId) {
      setErrorMessage('Pick a Central Team to link.');
      return;
    }
    try {
      await updateCircle({
        id: circle.id,
        data: { centralTeamId: selectedId },
      }).unwrap();
      onResult({ kind: 'success', message: 'Central Team linked.' });
    } catch (err: any) {
      setErrorMessage(
        err?.data?.error || err?.data?.message || 'Failed to link Central Team.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="link-central-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isSaving) onClose();
      }}
      data-testid="link-central-team-modal"
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-md focus:outline-none">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2 id="link-central-title" className="flex-1 text-sm font-semibold text-slate-900">
            Link a Central Team
          </h2>
        </div>
        <div className="p-4 space-y-3">
          {!available && (
            <p className="text-xs text-slate-500" data-testid="link-central-unavailable">
              Central Team service isn&apos;t configured for this environment.
            </p>
          )}
          {available && isLoading && <p className="text-xs text-slate-500">Loading teams…</p>}
          {available && isError && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-red-600">Failed to load Central teams.</p>
              <button type="button" onClick={() => refetch()} className="text-xs underline">
                Retry
              </button>
            </div>
          )}
          {available && !isLoading && !isError && teams.length === 0 && (
            <p className="text-xs text-slate-500" data-testid="link-central-empty">
              No Central Teams exist in this workspace yet.
            </p>
          )}
          {available && teams.length > 0 && (
            <div>
              <label
                htmlFor="link-central-select"
                className="block text-[11px] font-medium text-slate-700 mb-1"
              >
                Central Team
              </label>
              <select
                id="link-central-select"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900"
                data-testid="link-central-select"
              >
                <option value="">Select a team…</option>
                {teams
                  .filter((t) => t.status === 'active')
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {errorMessage && (
            <p role="alert" className="text-xs text-red-600" data-testid="link-central-error">
              {errorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedId || isSaving}
            data-testid="link-central-submit"
          >
            {isSaving ? 'Linking…' : 'Link'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Central Team view — read-only. No Add/Remove, no Contact picker.
// -----------------------------------------------------------------------------

function CentralTeamPeopleView({
  circle,
  members,
  canManage,
  centralError,
  onRetry,
}: {
  circle: Circle;
  members: CircleCentralMember[];
  canManage: boolean;
  centralError?: 'not_found' | 'forbidden' | 'unavailable';
  onRetry: () => void;
}) {
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<CircleCentralMember | null>(null);
  const [managingProvidersFor, setManagingProvidersFor] =
    useState<CircleCentralMember | null>(null);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [removeMember, { isLoading: isRemoving }] = useRemoveCircleCentralMemberMutation();

  // Phase 12.8 — Circle-native voice-token test. The button appears
  // ONLY on the current user's OWN row and only when that user has a
  // voice pin. Server derives userId from req.user.id, so the button
  // cannot mint on anyone else's behalf.
  const { user: currentUser } = useAuth();
  const [mintVoiceToken, { isLoading: isMinting }] =
    useMintCircleVoiceTokenMutation();

  // Phase 12.9 — Circle-native voice CALL modal state. The dialer is
  // shown only on the current user's OWN row. Voice permission and
  // device-readiness gate whether the button is enabled — same
  // gates PhonePage's dialer uses.
  const [circleCallOpen, setCircleCallOpen] = useState(false);
  const { canMakeCalls } = useTelephonyAvailability();
  const voice = useTwilioVoiceDevice();

  const handleTestVoice = async () => {
    setBanner(null);
    try {
      const result = await mintVoiceToken({ circleId: circle.id }).unwrap();
      // Never print the provider id; name the provider when this member's
      // own voice pin is what resolved.
      const providerName = result.outboundProviderId
        ? (assignmentsByUserId[currentUser?.id ?? ''] ?? []).find(
            (a) => a.channelType === 'voice' && a.outboundProviderId === result.outboundProviderId,
          )?.providerDisplayName ?? null
        : null;
      setBanner({
        kind: 'success',
        message: providerName
          ? `Voice is ready (${providerName}).`
          : result.outboundProviderId
            ? 'Voice is ready.'
            : 'Voice is ready, using the workspace default.',
      });
    } catch (err: any) {
      // A5.8 — prefer the entitlement code's copy over the generic
      // server string, so PROVIDER_NOT_ENTITLED reads as an actionable
      // authorization decision rather than a mint failure.
      const { message } = readEntitlementError(err);
      setBanner({ kind: 'error', message });
    }
  };

  // Phase 12.7 — bulk read of per-member provider pins for the People
  // tab. Skipped when Central is unreachable so we don't ping the BFF
  // with nothing to show. Failures degrade to empty (the service
  // swallows upstream errors), so this never blocks the People render.
  const membersProvidersQuery = useGetCirclePeopleProvidersQuery(
    { circleId: circle.id },
    { skip: !!centralError || members.length === 0 },
  );
  const assignmentsByUserId =
    membersProvidersQuery.data?.assignmentsByUserId ?? {};

  // A5.6 — workspace provider catalog for the Provider-access picker.
  // Reuses the SAME endpoint the pin modal reads, so grant options and
  // pin options can never drift apart. Skipped when Central is
  // unreachable, matching the pin query above.
  const circleProvidersQuery = useGetCircleProvidersQuery(circle.id, {
    skip: !!centralError || members.length === 0,
  });
  const providerCatalog = useMemo(
    () => deriveProviderCatalog(circleProvidersQuery.data?.integrations),
    [circleProvidersQuery.data],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return members;
    const needle = query.trim().toLowerCase();
    return members.filter((m) =>
      `${m.name || ''} ${m.email || ''}`.toLowerCase().includes(needle),
    );
  }, [members, query]);

  // Removal asks first in the shared ConfirmDialog (it used a blocking
  // window.confirm). A refusal stays inside the dialog where the user is
  // looking; success closes it and reports on the page banner.
  const [memberToRemove, setMemberToRemove] = useState<CircleCentralMember | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleRemove = (m: CircleCentralMember) => {
    setBanner(null);
    setRemoveError(null);
    setMemberToRemove(m);
  };

  const confirmRemove = async () => {
    const m = memberToRemove;
    if (!m) return;
    try {
      await removeMember({ circleId: circle.id, membershipId: m.membershipId }).unwrap();
      setMemberToRemove(null);
      setBanner({ kind: 'success', message: 'Member removed.' });
    } catch (err: any) {
      setRemoveError(err?.data?.error || err?.data?.message || 'Failed to remove member.');
    }
  };

  return (
    <div
      className="space-y-4"
      data-testid="circle-tabpanel-people"
      data-people-source="central_team"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
        <div>
          <span className="font-medium">Central Team ({members.length})</span>
          <span className="ml-2">Team membership is managed in Central.</span>
        </div>
        {canManage && !centralError && (
          <Button
            onClick={() => setShowAdd(true)}
            data-testid="circle-add-central-users-btn"
          >
            Add Central users
          </Button>
        )}
      </div>

      {banner && (
        <div
          role="alert"
          className={`rounded-lg border px-3 py-2 text-sm ${
            banner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {banner.message}
        </div>
      )}

      {centralError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start justify-between gap-3"
          data-testid={`circle-people-central-error-${centralError}`}
        >
          <p>
            {centralError === 'not_found'
              ? 'Linked Central Team no longer exists.'
              : centralError === 'forbidden'
                ? "You don't have permission to view this Central Team's members."
                : `${typeLabel(circle.type)} members are temporarily unavailable. Try again in a moment.`}
          </p>
          {centralError === 'unavailable' && (
            <button
              type="button"
              onClick={onRetry}
              className="text-xs font-medium text-red-800 underline underline-offset-2 hover:text-red-900"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {!centralError && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search members..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Search members"
          data-testid="circle-people-search"
        />
      )}

      {!centralError && filtered.length === 0 ? (
        <div
          className="text-center py-10 bg-white rounded-xl shadow-sm"
          data-testid="circle-people-empty"
        >
          <p className="text-gray-500">
            {members.length === 0
              ? 'No members in this Central Team.'
              : 'No members match your search.'}
          </p>
        </div>
      ) : (
        !centralError && (
          <ul className="divide-y divide-gray-100 bg-white rounded-xl shadow-sm">
            {filtered.map((m) => (
              <li
                key={m.membershipId}
                className="flex items-center justify-between px-4 py-3 gap-3"
                data-testid={`circle-central-member-row-${m.membershipId}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {m.name || m.email || 'Unknown user'}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {m.email && <span>{m.email}</span>}
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {m.role}
                    </span>
                    {m.isPrimary && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        Primary
                      </span>
                    )}
                  </div>
                  {/* A5.6 — Provider access (Central grant) sits ABOVE
                      the Circle routing chips and is visually distinct.
                      Different system, different meaning: "may USE this
                      provider across apps" vs "prefer it in this
                      Circle". Neither control writes the other's store. */}
                  <MemberProviderAccessSection
                    circleId={circle.id}
                    memberUserId={m.userId}
                    memberLabel={m.name || m.email || 'Unknown member'}
                    isHead={circle.canManageAsHead === true}
                    isSelf={currentUser?.id === m.userId}
                    providers={providerCatalog}
                  />
                  <MemberProviderChips
                    memberUserId={m.userId}
                    assignments={assignmentsByUserId[m.userId] ?? []}
                  />
                </div>
                {currentUser?.id === m.userId &&
                  (assignmentsByUserId[m.userId] ?? []).some(
                    (a) => a.channelType === 'voice',
                  ) && (
                    <button
                      type="button"
                      disabled={isMinting}
                      onClick={handleTestVoice}
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded px-2 py-1 disabled:opacity-50"
                      data-testid={`circle-central-test-voice-${m.membershipId}`}
                      title="Mint a short-lived voice token bound to your Circle pin"
                    >
                      {isMinting ? 'Testing…' : 'Test my voice'}
                    </button>
                  )}
                {/* Phase 12.9 — Call from this Circle. Own row only.
                    Requires the existing `phone.call` capability and a
                    ready browser Voice device. Head status is NEVER
                    what unlocks the call — it's the caller's own
                    capability + membership. */}
                {currentUser?.id === m.userId && canMakeCalls && (
                  <button
                    type="button"
                    disabled={!voice.isReady || voice.activeCall.kind !== 'idle'}
                    onClick={() => {
                      setBanner(null);
                      setCircleCallOpen(true);
                    }}
                    className="text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded px-2 py-1 disabled:opacity-50"
                    data-testid={`circle-central-call-from-circle-${m.membershipId}`}
                    title={
                      !voice.isReady
                        ? 'The browser calling device is not ready.'
                        : voice.activeCall.kind !== 'idle'
                          ? 'A call is already in progress.'
                          : 'Place a Circle-native call using your assigned provider.'
                    }
                  >
                    Call from this Circle
                  </button>
                )}
                {canManage && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setBanner(null);
                        setManagingProvidersFor(m);
                      }}
                      className="text-xs font-medium text-indigo-700 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2 py-1"
                      data-testid={`circle-central-manage-providers-${m.membershipId}`}
                    >
                      Manage providers
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBanner(null);
                        setEditing(m);
                      }}
                      className="text-xs font-medium text-indigo-700 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2 py-1"
                      data-testid={`circle-central-change-role-${m.membershipId}`}
                    >
                      Change role
                    </button>
                    <button
                      type="button"
                      disabled={isRemoving}
                      onClick={() => handleRemove(m)}
                      className="text-xs font-medium text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1 disabled:opacity-50"
                      aria-label={`Remove ${m.name || m.email || 'member'}`}
                      data-testid={`circle-central-remove-${m.membershipId}`}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      <ConfirmDialog
        isOpen={!!memberToRemove}
        title="Remove member?"
        message={`Remove ${memberToRemove?.name || memberToRemove?.email || 'this member'} from the Central Team?`}
        confirmLabel="Remove"
        variant="danger"
        error={removeError}
        onConfirm={confirmRemove}
        onCancel={() => {
          setMemberToRemove(null);
          setRemoveError(null);
        }}
      />
      {showAdd && (
        <AddCentralUsersModal
          circleId={circle.id}
          onClose={() => setShowAdd(false)}
          onResult={(result) => {
            setBanner(result);
            setShowAdd(false);
          }}
        />
      )}
      {editing && (
        <ChangeCentralRoleModal
          circleId={circle.id}
          member={editing}
          onClose={() => setEditing(null)}
          onResult={(result) => {
            setBanner(result);
            setEditing(null);
          }}
        />
      )}
      {managingProvidersFor && (
        <ManageMemberProvidersModal
          circleId={circle.id}
          memberUserId={managingProvidersFor.userId}
          memberDisplayName={
            managingProvidersFor.name ||
            managingProvidersFor.email ||
            'Unknown member'
          }
          onClose={() => setManagingProvidersFor(null)}
        />
      )}
      {circleCallOpen && (
        <CircleVoiceCallModal
          circleId={circle.id}
          circleName={circle.name}
          assignedProviderHint={
            (assignmentsByUserId[currentUser?.id ?? ''] ?? []).find(
              (a) => a.channelType === 'voice',
            )?.providerDisplayName ?? null
          }
          assignedProviderId={
            (assignmentsByUserId[currentUser?.id ?? ''] ?? []).find(
              (a) => a.channelType === 'voice',
            )?.outboundProviderId ?? null
          }
          onClose={() => setCircleCallOpen(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AddCentralUsersModal — pulls workspace users from Central, hides existing
// team members, and calls the CRM proxy that forwards to Central's SDK.
// -----------------------------------------------------------------------------

function AddCentralUsersModal({
  circleId,
  onClose,
  onResult,
}: {
  circleId: string;
  onClose: () => void;
  onResult: (b: { kind: 'success' | 'error'; message: string }) => void;
}) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [role, setRole] = useState<CentralTeamRole>('Member');
  const [isPrimary, setIsPrimary] = useState(false);
  const [query, setQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useGetCirclePeopleCandidatesQuery(circleId);
  const [addMember, { isLoading: isAdding }] = useAddCircleCentralMemberMutation();
  const dialogRef = useCircleDialogFocus(onClose, isAdding);

  const candidates: CircleCentralCandidate[] = data?.candidates ?? [];
  const eligible = useMemo(
    () => candidates.filter((c) => !c.existingTeamMembership),
    [candidates],
  );
  const visible = useMemo(() => {
    if (!query.trim()) return eligible;
    const needle = query.trim().toLowerCase();
    return eligible.filter((c) =>
      `${c.name || ''} ${c.email || ''}`.toLowerCase().includes(needle),
    );
  }, [eligible, query]);

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (!selectedId) {
      setErrorMessage('Pick a user to add.');
      return;
    }
    try {
      await addMember({
        circleId,
        body: { userId: selectedId, role, isPrimary },
      }).unwrap();
      onResult({ kind: 'success', message: 'Central user added to the team.' });
    } catch (err: any) {
      const code = err?.data?.code;
      const message =
        err?.data?.error ||
        err?.data?.message ||
        (code === 'ALREADY_MEMBER'
          ? 'That user is already a member.'
          : code === 'CENTRAL_FORBIDDEN'
            ? "You don't have permission to add members to this Central Team."
            : 'Failed to add member.');
      setErrorMessage(message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-central-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isAdding) onClose();
      }}
      data-testid="add-central-users-modal"
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col focus:outline-none">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2 id="add-central-title" className="flex-1 text-sm font-semibold text-slate-900">
            Add Central users
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isAdding}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto min-h-0">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search workspace users..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Search workspace users"
            data-testid="add-central-search"
          />

          {isLoading && <p className="text-xs text-slate-500">Loading users…</p>}
          {isError && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-red-600">Failed to load workspace users.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-xs underline"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && visible.length === 0 && (
            <p className="text-xs text-slate-500" data-testid="add-central-empty">
              {eligible.length === 0
                ? 'Every active workspace user is already in this Central Team.'
                : 'No workspace users match your search.'}
            </p>
          )}

          {visible.length > 0 && (
            <ul
              className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-72 overflow-y-auto"
              role="radiogroup"
              aria-label="Workspace users"
            >
              {visible.map((c) => (
                <li key={c.userId}>
                  <label
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                      selectedId === c.userId ? 'bg-indigo-50' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`add-central-user-${c.userId}`}
                  >
                    <input
                      type="radio"
                      name="add-central-user"
                      value={c.userId}
                      checked={selectedId === c.userId}
                      onChange={() => setSelectedId(c.userId)}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {c.name || c.email || 'Unknown user'}
                      </p>
                      {c.email && (
                        <p className="text-xs text-slate-500 truncate">{c.email}</p>
                      )}
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="add-central-role" className="block text-[11px] font-medium text-slate-700 mb-1">
                Role
              </label>
              <select
                id="add-central-role"
                value={role}
                onChange={(e) => setRole(e.target.value as CentralTeamRole)}
                className="block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900"
                data-testid="add-central-role-select"
              >
                {CENTRAL_TEAM_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  className="h-4 w-4 text-indigo-600"
                  data-testid="add-central-primary"
                />
                Primary team for this user
              </label>
            </div>
          </div>

          {errorMessage && (
            <p role="alert" className="text-xs text-red-600" data-testid="add-central-error">
              {errorMessage}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isAdding}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedId || isAdding}
            data-testid="add-central-submit"
          >
            {isAdding ? 'Adding…' : 'Add to team'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ChangeCentralRoleModal — role + isPrimary editor for an existing member.
// -----------------------------------------------------------------------------

function ChangeCentralRoleModal({
  circleId,
  member,
  onClose,
  onResult,
}: {
  circleId: string;
  member: CircleCentralMember;
  onClose: () => void;
  onResult: (b: { kind: 'success' | 'error'; message: string }) => void;
}) {
  const [role, setRole] = useState<CentralTeamRole>(member.role as CentralTeamRole);
  const [isPrimary, setIsPrimary] = useState(member.isPrimary);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updateMember, { isLoading }] = useUpdateCircleCentralMemberMutation();
  const dialogRef = useCircleDialogFocus(onClose, isLoading);

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (role === member.role && isPrimary === member.isPrimary) {
      onClose();
      return;
    }
    try {
      await updateMember({
        circleId,
        membershipId: member.membershipId,
        body: {
          ...(role !== member.role ? { role } : {}),
          ...(isPrimary !== member.isPrimary ? { isPrimary } : {}),
        },
      }).unwrap();
      onResult({ kind: 'success', message: 'Member updated.' });
    } catch (err: any) {
      setErrorMessage(
        err?.data?.error ||
          err?.data?.message ||
          'Failed to update member.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-central-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
      data-testid="change-central-role-modal"
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-sm focus:outline-none">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2 id="edit-central-title" className="flex-1 text-sm font-semibold text-slate-900">
            Change role · {member.name || member.email || 'member'}
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label
              htmlFor="edit-central-role"
              className="block text-[11px] font-medium text-slate-700 mb-1"
            >
              Role
            </label>
            <select
              id="edit-central-role"
              value={role}
              onChange={(e) => setRole(e.target.value as CentralTeamRole)}
              className="block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900"
              data-testid="edit-central-role-select"
            >
              {CENTRAL_TEAM_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="h-4 w-4 text-indigo-600"
              data-testid="edit-central-primary"
            />
            Primary team for this user
          </label>
          {errorMessage && (
            <p role="alert" className="text-xs text-red-600">
              {errorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            data-testid="edit-central-submit"
          >
            {isLoading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Contact view — historical editor (Add contacts / Remove).
// -----------------------------------------------------------------------------

function ContactPeopleView({
  circle,
  workspaceId,
  members,
}: {
  circle: Circle;
  workspaceId?: string;
  members: CircleContactMember[];
}) {
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [removeCircleMember, { isLoading: isRemoving }] = useRemoveCircleMemberMutation();

  const filtered = useMemo(() => {
    if (!query.trim()) return members;
    const needle = query.trim().toLowerCase();
    return members.filter((m) => {
      const name =
        m.contact?.fullName || m.user?.fullName || m.externalEmail || m.label || '';
      return name.toLowerCase().includes(needle);
    });
  }, [members, query]);

  return (
    <div
      className="space-y-4"
      data-testid="circle-tabpanel-people"
      data-people-source="crm_contacts"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search members..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Search members"
          data-testid="circle-people-search"
        />
        <Button onClick={() => setShowAdd(true)} data-testid="circle-add-contacts-btn">
          Add contacts
        </Button>
      </div>

      {banner && (
        <div
          role="alert"
          className={`rounded-lg border px-3 py-2 text-sm ${
            banner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {banner.message}
        </div>
      )}

      {filtered.length === 0 ? (
        <div
          className="text-center py-10 bg-white rounded-xl shadow-sm"
          data-testid="circle-people-empty"
        >
          <p className="text-gray-500">
            {members.length === 0 ? 'No members yet.' : 'No members match your search.'}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 bg-white rounded-xl shadow-sm">
          {filtered.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between px-4 py-3 gap-3"
              data-testid={`circle-member-row-${m.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {m.contact?.fullName ||
                    m.user?.fullName ||
                    m.externalEmail ||
                    m.label ||
                    'Unknown member'}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <MembershipChip label={m.membershipStatus} />
                  <MembershipChip label={m.membershipType} tone="neutral" />
                  {m.isPrimaryDepartment && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      Primary
                    </span>
                  )}
                  {m.startDate && <span>Since {m.startDate}</span>}
                  {m.endDate && <span>Until {m.endDate}</span>}
                  {m.addedVia && <span>via {m.addedVia}</span>}
                </div>
                {m.notes && <p className="mt-1 text-xs text-gray-600">{m.notes}</p>}
              </div>
              <button
                type="button"
                disabled={isRemoving}
                onClick={async () => {
                  setBanner(null);
                  try {
                    await removeCircleMember({
                      circleId: circle.id,
                      memberId: m.id,
                    }).unwrap();
                    setBanner({ kind: 'success', message: 'Member removed.' });
                  } catch (err: any) {
                    setBanner({
                      kind: 'error',
                      message:
                        err?.data?.error ||
                        err?.data?.message ||
                        'Failed to remove member.',
                    });
                  }
                }}
                className="text-sm text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1"
                aria-label={`Remove ${m.contact?.fullName || m.user?.fullName || 'member'}`}
                data-testid={`circle-remove-member-${m.id}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd && workspaceId && (
        <AddContactsModal
          circleId={circle.id}
          workspaceId={workspaceId}
          existingContactIds={new Set(
            members.map((m) => m.contactId).filter((v): v is string => !!v),
          )}
          onClose={() => setShowAdd(false)}
          onResult={(result) => {
            setBanner({
              kind: result.added > 0 ? 'success' : 'error',
              message:
                result.added > 0
                  ? `Added ${result.added} contact${result.added === 1 ? '' : 's'}.${
                      result.skipped > 0
                        ? ` (${result.skipped} skipped)`
                        : ''
                    }`
                  : 'No contacts were added.',
            });
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
}

function MembershipChip({
  label,
  tone = 'default',
}: {
  label: string | null;
  tone?: 'default' | 'neutral';
}) {
  if (!label) return null;
  const color =
    tone === 'neutral'
      ? 'bg-gray-100 text-gray-700'
      : label === 'active'
        ? 'bg-green-100 text-green-700'
        : label === 'pending'
          ? 'bg-yellow-100 text-yellow-700'
          : 'bg-gray-100 text-gray-700';
  return <span className={`px-2 py-0.5 rounded-full ${color}`}>{label}</span>;
}

function AddContactsModal({
  circleId,
  workspaceId,
  existingContactIds,
  onClose,
  onResult,
}: {
  circleId: string;
  workspaceId: string;
  existingContactIds: Set<string>;
  onClose: () => void;
  onResult: (result: { added: number; skipped: number }) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [addMembers, { isLoading }] = useAddCircleMembersMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useCircleDialogFocus(onClose, isLoading);
  const { data: contactsResponse, isLoading: contactsLoading } = useGetContactsQuery(
    { workspaceId },
    { skip: !workspaceId },
  );
  const contacts: Contact[] = contactsResponse?.contacts || [];

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return contacts.filter((c) => {
      if (existingContactIds.has(c.id)) return false;
      if (!needle) return true;
      return (
        c.fullName.toLowerCase().includes(needle) ||
        (c.email || '').toLowerCase().includes(needle)
      );
    });
  }, [contacts, query, existingContactIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (selected.size === 0) {
      setErrorMessage('Select at least one contact.');
      return;
    }
    try {
      const response = await addMembers({
        circleId,
        members: Array.from(selected).map((contactId) => ({ contactId })),
      }).unwrap();
      onResult({
        added: response.added.length,
        skipped: response.skipped.length,
      });
    } catch (err: any) {
      setErrorMessage(
        err?.data?.error || err?.data?.message || 'Failed to add contacts.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-contacts-title"
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto focus:outline-none">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b">
            <h2 id="add-contacts-title" className="text-lg font-semibold text-gray-900">
              Add contacts to circle
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Contacts already in this circle are hidden. Duplicates cannot be added.
            </p>
          </div>
          <div className="p-6 space-y-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Search contacts"
            />
            <div
              className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto"
              data-testid="add-contacts-list"
            >
              {contactsLoading ? (
                <div className="p-6 flex justify-center"><LoadingSpinner /></div>
              ) : visible.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">
                  {existingContactIds.size > 0
                    ? 'No other contacts to add.'
                    : 'No contacts match your search.'}
                </p>
              ) : (
                visible.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.fullName}</p>
                      {c.email && (
                        <p className="text-xs text-gray-500 truncate">{c.email}</p>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
            {errorMessage && (
              <p role="alert" className="text-sm text-red-600">
                {errorMessage}
              </p>
            )}
          </div>
          <div className="p-6 border-t flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || selected.size === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
              data-testid="add-contacts-submit"
            >
              {isLoading ? 'Adding…' : `Add ${selected.size || ''}`.trim()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity tab
// ---------------------------------------------------------------------------

function ActivityTab({ circleId }: { circleId: string }) {
  const { data, isLoading, error } = useGetEntityActivityQuery({
    entityType: 'circle',
    entityId: circleId,
    limit: 50,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorState message="Failed to load activity." />;
  }
  const activities = data?.activities ?? [];
  if (activities.length === 0) {
    return (
      <div className="text-center py-10 bg-white rounded-xl shadow-sm" data-testid="circle-activity-empty">
        <p className="text-gray-500">No activity yet.</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-gray-100 bg-white rounded-xl shadow-sm" data-testid="circle-tabpanel-activity">
      {activities.map((a) => (
        <li key={a.id} className="px-4 py-3">
          <p className="text-sm text-gray-900">{a.summary}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {a.action} · {new Date(a.createdAt).toLocaleString()}
          </p>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Settings tab
// ---------------------------------------------------------------------------

function SettingsTab({
  circle,
  workspaceId,
  onDeleted,
}: {
  circle: Circle;
  workspaceId?: string;
  onDeleted: () => void;
}) {
  const [values, setValues] = useState<CircleFormValues>(circleToFormValues(circle));
  // What the rules looked like when the form opened: an unchanged rule set is
  // not re-sent, so a legacy Smart Circle without rules can still be renamed.
  const [initialRules] = useState(() => ({ type: circle.type, drafts: circleToFormValues(circle).smartRules ?? [] }));
  const [ruleErrors, setRuleErrors] = useState<ContactFilterError[] | undefined>(undefined);
  const owners = useCircleOwnerCandidates(workspaceId);
  const { user: currentUser } = useAuth();
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [updateCircle, { isLoading: isSaving }] = useUpdateCircleMutation();
  const [archiveCircle, { isLoading: isArchiving }] = useArchiveCircleMutation();
  const [restoreCircle, { isLoading: isRestoring }] = useRestoreCircleMutation();
  const [deleteCircle, { isLoading: isDeleting }] = useDeleteCircleMutation();

  const { data: allCircles = [] } = useGetCirclesQuery(
    { workspaceId, includeArchived: false },
    { skip: !workspaceId },
  );
  const candidateParents = useMemo(
    () => allCircles.filter((c) => c.id !== circle.id),
    [allCircles, circle.id],
  );

  // Phase 12 — the SettingsTab picker only shows Teams where the user
  // is Head. Non-Heads see no linkable Teams (and cannot save changes
  // to `centralTeamId` — backend re-verifies).
  const { data: teamsData } = useGetWorkspaceCentralTeamsQuery({ onlyHeaded: true });

  // Phase 12 — for a Central-backed department/team Circle, all
  // Circle-management mutations require Head-of-that-team. UI hides
  // the controls when `canManageAsHead === false`. Backend still
  // re-verifies; a client that renders these anyway will get 403.
  //
  //   isCentralBacked && canManageAsHead === false  → hide controls
  //   isCentralBacked && canManageAsHead === true   → show
  //   !isCentralBacked (CRM-native / linkless-org)  → show (legacy
  //     circles.manage capability gates on the server; UI defers)
  //   canManageAsHead === null/undefined            → show (defer to
  //     server; safest to render and let backend decide)
  const isCentralBacked =
    (circle.type === 'department' || circle.type === 'team') && !!circle.centralTeamId;
  const canManageThisCircle =
    !isCentralBacked || circle.canManageAsHead !== false;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);
    setRuleErrors(undefined);
    const smart = smartRulesForSubmit(values.type, values.smartRules, initialRules);
    if (!smart.ok) {
      setRuleErrors(smart.errors);
      setBanner({ kind: 'error', message: 'Finish the highlighted rules.' });
      return;
    }
    const body: UpdateCircleRequest = {
      name: values.name.trim(),
      description: values.description.trim() || null,
      color: values.color.trim() || null,
      icon: values.icon.trim() || null,
      type: values.type,
      visibility: values.visibility,
      ownerUserId: values.ownerUserId || null,
      parentCircleId: values.parentCircleId || null,
      centralTeamId: values.centralTeamId || null,
      ...(smart.rules ? { smartRules: smart.rules } : {}),
    };
    try {
      await updateCircle({ id: circle.id, data: body }).unwrap();
      setBanner({ kind: 'success', message: 'Saved.' });
    } catch (err: any) {
      const fieldErrors = smartRuleErrorsFrom(err);
      if (fieldErrors) {
        setRuleErrors(fieldErrors);
        setBanner({ kind: 'error', message: 'Some rules need attention.' });
        return;
      }
      setBanner({
        kind: 'error',
        message: err?.data?.error || err?.data?.message || 'Failed to save.',
      });
    }
  };

  const handleArchive = async () => {
    setBanner(null);
    try {
      await archiveCircle(circle.id).unwrap();
      setBanner({ kind: 'success', message: 'Circle archived.' });
    } catch (err: any) {
      setBanner({
        kind: 'error',
        message: err?.data?.error || err?.data?.message || 'Failed to archive.',
      });
    }
  };

  const handleRestore = async () => {
    setBanner(null);
    try {
      await restoreCircle(circle.id).unwrap();
      setBanner({ kind: 'success', message: 'Circle restored.' });
    } catch (err: any) {
      setBanner({
        kind: 'error',
        message: err?.data?.error || err?.data?.message || 'Failed to restore.',
      });
    }
  };

  const handleDelete = async () => {
    setBanner(null);
    try {
      await deleteCircle(circle.id).unwrap();
      onDeleted();
    } catch (err: any) {
      setBanner({
        kind: 'error',
        message: err?.data?.error || err?.data?.message || 'Failed to delete.',
      });
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="circle-tabpanel-settings">
      {isCentralBacked && !canManageThisCircle && (
        <div
          role="status"
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
          data-testid="circle-settings-head-required-notice"
        >
          Only the Head of this Central Team can edit, archive, or delete this Circle.
        </div>
      )}

      {canManageThisCircle && (
        <form onSubmit={handleSave} className="bg-white p-6 rounded-xl shadow-sm space-y-4">
          <CircleFormFields
            values={values}
            onChange={setValues}
            candidateParents={candidateParents}
            workspaceMembers={owners.members}
            ownerCandidatesStatus={owners.status}
            currentUserId={currentUser?.id ?? null}
            centralTeams={teamsData?.teams}
            centralTeamsUnavailable={teamsData?.available === false}
            disabled={isSaving}
            ruleErrors={ruleErrors}
          />
          <div className="flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
              data-testid="circle-settings-save"
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      {canManageThisCircle && (
        <div className="bg-white p-6 rounded-xl shadow-sm space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Lifecycle</h3>
          <p className="text-sm text-gray-600">
            Archiving hides the circle from the default list but keeps all history. Deleting is a soft-delete;
            the circle can be restored later. History and audit are preserved either way.
          </p>
          <div className="flex flex-wrap gap-3">
            {circle.archivedAt ? (
              <Button
                onClick={handleRestore}
                disabled={isRestoring}
                data-testid="circle-restore-btn"
              >
                {isRestoring ? 'Restoring…' : 'Restore circle'}
              </Button>
            ) : (
              <Button
                onClick={handleArchive}
                disabled={isArchiving}
                data-testid="circle-archive-btn"
              >
                {isArchiving ? 'Archiving…' : 'Archive circle'}
              </Button>
            )}
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={isDeleting}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-60"
              data-testid="circle-delete-btn"
            >
              Delete circle
            </button>
          </div>
        </div>
      )}

      {banner && (
        <div
          role="alert"
          className={`rounded-lg border px-3 py-2 text-sm ${
            banner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {banner.message}
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={confirmDelete}
        itemName={circle.name}
        itemType="circle"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
